import { CopyToClipboard as ReactCopyToClipboard } from 'react-copy-to-clipboard';
import type { KeyboardEvent, MouseEvent, VoidFunctionComponent } from 'react';

import { useCopyToClipboard } from '../../../hooks/useCopyToClipboard';
import LabeledTextInput from '../Forms/LabeledTextInput';
import Icon, { icons } from '../Icon';
import type { TextInputProps } from '../Forms/TextInput';

import styles from './InlineCopiableText.module.less';

type Props = TextInputProps & {
  /**
   * When true, clicking the field opens its value as a URL in a new tab. The
   * copy affordance stays on the trailing icon (clicking the icon copies rather
   * than navigating). Defaults to false, where clicking anywhere copies.
   */
  openLinkOnClick?: boolean;
};

/**
 * A read-only text input whose value can be copied to the clipboard. With
 * `openLinkOnClick`, the field also behaves as a link: clicking it opens the
 * value in a new tab, while the trailing icon still copies.
 */
const InlineCopiableText: VoidFunctionComponent<Props> = ({
  label,
  value,
  openLinkOnClick = false,
  ...props
}) => {
  const [isCopied, setIsCopied] = useCopyToClipboard();
  //  Inputs can be strings or numbers, this helps us ensure we're always working with a string.
  const valueAsString = value?.toString() ?? '';
  const copyIcon = <Icon icon={isCopied ? icons.check : icons.cloneO} testId="copy-to-clipboard-icon" />;

  if (openLinkOnClick) {
    const openLink = () => window.open(valueAsString, '_blank', 'noopener,noreferrer');
    return (
      <div
        className={styles.linkField}
        role="link"
        tabIndex={0}
        onClick={openLink}
        onKeyDown={(event: KeyboardEvent) => {
          if (event.key === 'Enter') {
            openLink();
          }
        }}
      >
        <LabeledTextInput
          // eslint-disable-next-line react/jsx-props-no-spreading -- There's a lot of props, so we want to spread them.
          {...props}
          label={label}
          value={valueAsString}
          suffix={
            <ReactCopyToClipboard text={valueAsString} onCopy={() => setIsCopied(true)}>
              {/* Copy on the icon only; stop the click from also opening the link. */}
              <button
                type="button"
                className={styles.copyButton}
                aria-label="Copy"
                onClick={(event: MouseEvent) => event.stopPropagation()}
                onKeyDown={(event: KeyboardEvent) => event.stopPropagation()}
              >
                {copyIcon}
              </button>
            </ReactCopyToClipboard>
          }
          isReadOnly
        />
      </div>
    );
  }

  return (
    <ReactCopyToClipboard text={valueAsString} onCopy={() => setIsCopied(true)}>
      {/* This is required since it enforces that all children copy the text when clicked */}
      <div className="block">
        <LabeledTextInput
          // eslint-disable-next-line react/jsx-props-no-spreading -- There's a lot of props, so we want to spread them.
          {...props}
          label={label}
          value={valueAsString}
          suffix={copyIcon}
          isReadOnly
        />
      </div>
    </ReactCopyToClipboard>
  );
};

export default InlineCopiableText;
